import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/** After the box lands in the cart icon, a beat so the catch is seen. */
const AFTER_LANDING_MS = 240;
/** Longest the drawer waits for the box to land (product-form.js). */
const MAX_FLIGHT_WAIT_MS = 2600;
/** How long the line just added stays highlighted. */
const HIGHLIGHT_MS = 2600;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogCloseEvent.eventName, this.#clearHighlight);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogCloseEvent.eventName, this.#clearHighlight);
  }

  /**
   * Opens the drawer after an add to cart. When the product is still flying to
   * the cart icon (product-form.js), it waits for the landing first.
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    const detail = /** @type {any} */ (event).detail ?? {};
    const data = detail.data ?? {};
    const addedVariant = data.source === 'product-form-component' && !data.didError ? String(detail.sourceId ?? '') : '';

    const open = () => {
      this.showDialog();
      if (addedVariant) this.#highlight(addedVariant);
    };

    if (!document.querySelector('fly-to-cart.plu-fly:not([data-landed])')) {
      open();
      return;
    }

    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      document.removeEventListener('plu:fly-landed', onLanded);
      window.clearTimeout(timer);
      open();
    };
    const onLanded = () => window.setTimeout(go, AFTER_LANDING_MS);
    document.addEventListener('plu:fly-landed', onLanded, { once: true });
    const timer = window.setTimeout(go, MAX_FLIGHT_WAIT_MS);
  };

  /**
   * Highlights the line of the variant just added. The drawer's content is
   * refreshed on the same event, so it looks for the line until it is there.
   * @param {string} variantId
   */
  #highlight(variantId) {
    const id = variantId.replace(/\D/g, '');
    if (!id) return;
    let tries = 0;
    const find = () => {
      const row = this.querySelector(`.cart-items__table-row[data-key^="${id}:"]`);
      if (row) {
        row.classList.remove('plu-just-added');
        void (/** @type {HTMLElement} */ (row).offsetWidth);
        row.classList.add('plu-just-added');
        window.setTimeout(() => row.classList.remove('plu-just-added'), HIGHLIGHT_MS);
      } else if (++tries < 12) {
        window.setTimeout(find, 120);
      }
    };
    requestAnimationFrame(find);
  }

  #clearHighlight = () => {
    for (const row of this.querySelectorAll('.plu-just-added')) row.classList.remove('plu-just-added');
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
